import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import { getLaminariaSectionDetail } from '../../../../models/laminariaImages.js';

const LaminariaImageResourceDetail = ({ laminariaId, sectionId, imageId, backPath }) => {
  const navigate = useNavigate();
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const section = await getLaminariaSectionDetail(laminariaId, sectionId);
        const found = (section?.images || []).find(img => String(img.image_id) === String(imageId));
        if (mounted) {
          setImage(found || null);
        }
      } catch (err) {
        if (mounted) {
          setError(err?.response?.data?.message || 'No se pudo cargar la imagen seleccionada.');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [laminariaId, sectionId, imageId]);

  const handleBack = () => {
    navigate(backPath);
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display='flex' justifyContent='flex-end' mb={3}>
        <Button variant='contained' onClick={handleBack} startIcon={<ArrowBackIosIcon />}>
          Volver
        </Button>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!error && !image ? (
        <Alert severity='info'>No se encontró la imagen solicitada.</Alert>
      ) : (
        image && (
          <Card variant='outlined'>
            <CardMedia
              component='img'
              image={image.imageURL}
              alt={image.title}
              sx={{ maxHeight: 500, objectFit: 'contain', backgroundColor: 'action.hover' }}
            />
            <CardContent>
              <Typography variant='h4' gutterBottom>
                {image.title}
              </Typography>

              {image.indications?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant='h6' gutterBottom>
                    Indicaciones
                  </Typography>
                  <List>
                    {image.indications.map((indication, index) => (
                      <ListItem key={`indication-${image.image_id}-${index}`}>
                        <ListItemText
                          primary={indication.description}
                          secondary={indication.meaning}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {image.markers?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant='h6' gutterBottom>
                    Marcadores
                  </Typography>
                  <List>
                    {image.markers.map((marker, index) => (
                      <ListItem key={`marker-${index}`}>
                        <ListItemText
                          primary={`Punto ${marker.PointNumber}`}
                          secondary={marker.PointElement}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {!image.indications?.length && !image.markers?.length && (
                <Typography color='text.secondary' sx={{ mt: 2 }}>
                  Esta imagen no tiene indicaciones ni marcadores.
                </Typography>
              )}
            </CardContent>
          </Card>
        )
      )}
    </Box>
  );
};

export default LaminariaImageResourceDetail;
